import videojs from 'video.js';
import type { VideoJsPlayer } from 'video.js';
import type { KollectiveKSDKInfoEvent, KollectiveSourcesReadyEvent } from './kollective.types';
import type { KollectivePluginOptions } from '../player.interface';
import { ErrorCode, PlayerError } from '../player-errors';
import { PromiseQueue } from '../../../utils/promise-queue';
import { loadScript } from '../../../utils/load-script';

const loadQueue = new PromiseQueue();

export function getKollectiveSources(
  player: VideoJsPlayer,
  config: KollectivePluginOptions,
  abortSignal: AbortSignal
): Promise<videojs.Tech.SourceObject[]> {
  const taskId = Math.random().toString(36).substring(7);

  return new Promise(
    async (resolve: (source: videojs.Tech.SourceObject[]) => void, reject: (reason: PlayerError) => void) => {
      // Abort if the signal is already aborted
      if (abortSignal.aborted) return;

      if (!window.ksdk) {
        await loadScript('https://cdn.kollective.app/sdk/ksdk-latest.min.js', 'mi-kollective-sdk');
      }

      const ksdkInstance = window.ksdk('VideoJS', player);

      // Stop delivery if the signal is aborted
      abortSignal.addEventListener('abort', () => ksdkInstance && ksdkInstance.stop(), { once: true });

      ksdkInstance.addEventListener('SourcesReady', (event: KollectiveSourcesReadyEvent) => {
        if (abortSignal.aborted) return;
        resolve(event.data.publishedSources);
      });
      ksdkInstance.addEventListener('KsdkError', () => {
        if (abortSignal.aborted) return;
        reject(new PlayerError(ErrorCode.PLAYER_ERR_P2P_SDK_ERROR, 'Kollective SDK Error'));
      });
      ksdkInstance.addEventListener('KsdkInfo', (event: KollectiveKSDKInfoEvent) => {
        if (abortSignal.aborted) return;
        if (config.debug) {
          console.log('KSDK emitted Information: ', event);
        }
      });

      loadQueue.enqueue({
        id: taskId,
        abortSignal,
        fn: () => ksdkInstance.load(config),
      });
    }
  );
}
